import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getTemplates } from '../../api/template';
import type { MailTemplateItem, SmsTemplateItem, TemplateListParams } from '../../api/template';
import { MailTemplateType, TemplateType } from '../../consts';
import type { AsyncStatus } from '../../interfaces';

export interface TemplateTab {
  type: TemplateType;
  mailTemplateType?: MailTemplateType;
}

interface TemplateState {
  status: AsyncStatus;
  list: MailTemplateItem[] | SmsTemplateItem[];
  activeTab: TemplateTab;
}

const initialState: TemplateState = {
  status: 'idle',
  list: [],
  // Default to first tab: Mail (社内通知)
  activeTab: { type: TemplateType.MAIL, mailTemplateType: MailTemplateType.TO_COMPANY },
};

export const fetchTemplateList = createAsyncThunk(
  'template/fetchList',
  async (params: TemplateListParams & { signal?: AbortSignal }) => {
    try {
      return await getTemplates(params);
    } catch (error) {
      // Silently ignore abort errors — they're expected when requests are cancelled
      if (error instanceof Error && error.name === 'AbortError') {
        return undefined;
      }
      throw error;
    }
  },
);

const templateSlice = createSlice({
  name: 'template',
  initialState,
  reducers: {
    reset: () => initialState,
    setActiveTab: (state, action: PayloadAction<TemplateTab>) => {
      state.activeTab = action.payload;
      state.list = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemplateList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchTemplateList.fulfilled, (state, action) => {
        // Ignore results from aborted requests (payload is undefined).
        // Leave the status untouched so a newer in-flight request (which has
        // already set status to 'loading') is not overwritten.
        if (action.payload !== undefined) {
          state.status = 'succeeded';
          state.list = action.payload;
        }
      })
      .addCase(fetchTemplateList.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export const { reset: resetTemplate, setActiveTab } = templateSlice.actions;
export default templateSlice.reducer;
